'use client'

import { useQueryResults } from "@/hooks/useQueryResults"
import { useMemo, useState } from "react"
import { AreaChart, ChartSeries, ChartDataPoint } from "./charts/area-chart"
import { DatasetAreaChart } from "./charts/dataset-area-chart"
import { Percent, Sigma } from "lucide-react"

export function SkyTokenRewardsBalance() {
    const { results } = useQueryResults({id: 5053707})

    const [showPct, setShowPct] = useState(false)
    const percentFormatter = new Intl.NumberFormat("en", {style: 'percent', maximumFractionDigits: 2})
    const formatter = new Intl.NumberFormat("en", {currency: "USD", currencyDisplay: 'narrowSymbol', style: 'currency', maximumFractionDigits: 0})
    const axisFormatter = new Intl.NumberFormat('en', {notation: 'compact', currency: "USD", currencyDisplay: 'narrowSymbol', style: 'currency'})
    
    const dataset = useMemo(() => {
        if (!results) return {dimensions: [], source: []}
        
        const series = [...new Set(results.map(r => r.group as string)).keys()]
        const days = [...new Set(results.map(r => r.block_date!.valueOf()))]

        return {
            dimensions: ['date', ...series],
            source: days.map(d => {
                const filtered = results.filter(r => r.block_date.valueOf() === d)
                return [new Date(d), ...series.map(s => filtered.find(r => r.group === s)?.balance ?? 0)]
            })
        }
    }, [results])

    const pctDataset = useMemo(() => {
        return {
            dimensions: dataset.dimensions,
            source: dataset.source.map(r => {
                const total = r.slice(1).reduce((a, i) => (a as number) + (i as number), 0) as number
                return [r[0], ...r.slice(1).map(v => (v as number) / total)]
            })
        }
    }, [dataset])

    return <div className="w-full h-[40vh]">
        <DatasetAreaChart
            dataset={showPct?pctDataset:dataset}
            title="SKY Token Rewards"
            description="Depositors in the USDS>SKY Farm"
            stack
            showTotal
            yAxisFormatter={(n) => showPct?percentFormatter.format(n):axisFormatter.format(n)}
            tooltip={{
                seriesValueFormatter: (value) => showPct?percentFormatter.format(value):`${formatter.format(value)}`
            }}
            legendActions={[
                {
                    name: `Switch to ${showPct?'nominal':'percent'} view`,
                    action: () => setShowPct(v => !v),
                    icon: showPct?Sigma:Percent
                }
            ]}
        />
    </div>
}

export function SpkTokenRewardsBalance() {
    const { results } = useQueryResults({id: 5376479})
    const [showPct, setShowPct] = useState(false)
    const percentFormatter = new Intl.NumberFormat("en", {style: 'percent', maximumFractionDigits: 2})
    const formatter = new Intl.NumberFormat("en", {currency: "USD", currencyDisplay: 'narrowSymbol', style: 'currency', maximumFractionDigits: 0})
    const axisFormatter = new Intl.NumberFormat('en', {notation: 'compact', currency: "USD", currencyDisplay: 'narrowSymbol', style: 'currency'})
    
    const dataset = useMemo(() => {
        if (!results) return {dimensions: [], source: []}
        
        const series = [...new Set(results.map(r => r.group as string)).keys()]
        const days = [...new Set(results.map(r => r.block_date!.valueOf()))]

        return {
            dimensions: ['date', ...series],
            source: days.map(d => {
                const filtered = results.filter(r => r.block_date.valueOf() === d)
                return [new Date(d), ...series.map(s => filtered.find(r => r.group === s)?.balance ?? 0)]
            })
        }
    }, [results])

    const pctDataset = useMemo(() => {
        return {
            dimensions: dataset.dimensions,
            source: dataset.source.map(r => {
                const total = r.slice(1).reduce((a, i) => (a as number) + (i as number), 0) as number
                return [r[0], ...r.slice(1).map(v => (v as number) / total)]
            })
        }
    }, [dataset])

    return <div className="w-full h-[40vh]">
        <DatasetAreaChart
            dataset={showPct?pctDataset:dataset}
            title="SPK Token Rewards"
            description="Depositors in the USDS>SPK Farm"
            stack
            showTotal
            yAxisFormatter={(n) => showPct?percentFormatter.format(n):axisFormatter.format(n)}
            tooltip={{
                seriesValueFormatter: (value) => showPct?percentFormatter.format(value):`${formatter.format(value)}`
            }}
            legendActions={[
                {
                    name: `Switch to ${showPct?'nominal':'percent'} view`,
                    action: () => setShowPct(v => !v),
                    icon: showPct?Sigma:Percent
                }
            ]}
        />
    </div>
}